import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import useOrgs from '@/src/Orgs/components/hooks/useOrgs';
import useWhitelabelSettings from '@/src/WhitelabelSettings/components/hooks/useWhitelabelSettings';
import AreaShell from '../../chrome/AreaShell';
import { Field, SaveBar, SectionCard, TextInput } from '../../components/forms';
import { Frow, useToast } from '../../components/roster';
import { TSkeleton } from '../../components/table';
import AssetPicker from '../../components/AssetPicker';
import './org.css';

/*
 * Org profile: name, logo, read-only facts. Saves via PUT /orgs/:id and
 * pushes the result into OrgContext so the rail's org mark updates.
 */

export default function OrgProfileV3() {
    const navigate = useNavigate();
    const { org, orgId, setOrg } = useUserOrg();
    const { updateOrg } = useOrgs();
    const { getOrgDomain } = useWhitelabelSettings();
    const api = useAuthenticatedApi();
    const toast = useToast();

    const savedName = org?.name ?? '';
    const savedLogo = org?.logo ?? '';
    const [name, setName] = useState(savedName);
    const [logo, setLogo] = useState(savedLogo);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [pickerOpen, setPickerOpen] = useState(false);
    const [domain, setDomain] = useState<string | null>(null);
    const [license, setLicense] = useState<string | null>(null);

    useEffect(() => {
        setName(org?.name ?? '');
        setLogo(org?.logo ?? '');
        setSaveError('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [org?.id]);

    useEffect(() => {
        if (!org?.id) return;
        let mounted = true;
        setDomain(null);
        setLicense(null);
        getOrgDomain(org.id)
            .then((base) => mounted && setDomain(base ?? ''))
            .catch(() => mounted && setDomain(''));
        /* GlobalAdmin-gated server-side; org admins just don't see the row */
        api.get(`/licenses/org/${orgId}`)
            .then((r: any) => {
                const active = (r.data ?? []).find((l: any) => !l.expired);
                if (mounted && active) setLicense(active.licenseType?.name ?? active.name ?? null);
            })
            .catch(() => {});
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [org?.id]);

    const nameMissing = name.trim() === '';
    const nameDirty = name.trim() !== savedName;
    const logoDirty = (logo || '') !== savedLogo;
    const dirty = useMemo(() => {
        const s: string[] = [];
        if (nameDirty) s.push('Name');
        if (logoDirty) s.push('Logo');
        return s;
    }, [nameDirty, logoDirty]);

    const save = async () => {
        if (saving || !orgId) return;
        if (nameMissing) {
            setSaveError('');
            return;
        }
        setSaving(true);
        setSaveError('');
        /* same bodies as v2: name alone, or name + logo when the logo changed */
        const body: { name: string; logo?: string | null } = logoDirty
            ? { name: nameDirty ? name.trim() : savedName.trim() || name.trim(), logo: logo || null }
            : { name: name.trim() };
        const updated = await updateOrg(orgId, body).catch(() => null);
        setSaving(false);
        if (updated?.id) {
            (setOrg as unknown as (o: unknown) => void)({ ...org, name: updated.name, logo: updated.logo });
            setName(updated.name ?? '');
            setLogo(updated.logo ?? '');
            toast.show('Organization saved.');
        } else {
            setSaveError('Couldn’t save — try again.');
        }
    };

    const discard = () => {
        setName(savedName);
        setLogo(savedLogo);
        setSaveError('');
    };

    return (
        <AreaShell area="org" tab="profile" sub={savedName || 'Name, logo, and details'}>
            <div className="org-pg">
                {!org ? (
                    <div className="sec">
                        <TSkeleton rows={3} />
                    </div>
                ) : (
                    <div className="stack">
                        <SectionCard title="Name">
                            <Field
                                label="Organization name"
                                help="Shown across the dashboard and in gallery emails."
                                error={nameMissing ? 'A name is required.' : undefined}
                            >
                                <TextInput
                                    value={name}
                                    maxLength={255}
                                    disabled={saving}
                                    aria-invalid={nameMissing ? true : undefined}
                                    style={{ maxWidth: 420 }}
                                    onChange={(e) => {
                                        setName(e.target.value);
                                        setSaveError('');
                                    }}
                                />
                            </Field>
                        </SectionCard>

                        <SectionCard title="Logo" note="square image recommended">
                            <div className="org-logo-row">
                                <span className="org-logo" aria-hidden={logo ? undefined : true}>
                                    {logo ? <img src={logo} alt="Organization logo" /> : (name.trim() || 'P')[0].toUpperCase()}
                                </span>
                                <button type="button" className="tb-btn" disabled={saving} onClick={() => setPickerOpen(true)}>
                                    {logo ? 'Change…' : 'Choose…'}
                                </button>
                                {logo && (
                                    <button
                                        type="button"
                                        className="tb-btn ghost"
                                        disabled={saving}
                                        onClick={() => {
                                            setLogo('');
                                            setSaveError('');
                                        }}
                                    >
                                        Remove
                                    </button>
                                )}
                            </div>
                            <p className="org-note">Shown in the sidebar and anywhere the organization appears.</p>
                        </SectionCard>

                        <SectionCard title="Details" note="read-only">
                            <Frow label="Org ID">
                                <span className="org-mono">{orgId}</span>
                            </Frow>
                            <Frow label="Gallery domain">
                                <span className="org-mono">
                                    {domain === null ? '…' : domain ? domain.replace(/^https?:\/\//, '') : '—'}
                                </span>
                                <button type="button" className="org-link" onClick={() => navigate('/org/whitelabel')}>
                                    Set in White-label
                                </button>
                            </Frow>
                            {license && (
                                <Frow label="License">
                                    <span className="org-mono">{license.toUpperCase()}</span>
                                </Frow>
                            )}
                        </SectionCard>

                        {saveError && (
                            <p className="field-e" role="alert">
                                {saveError}
                            </p>
                        )}
                    </div>
                )}

                <SaveBar dirty={dirty} saving={saving} onSave={save} onDiscard={discard} />

                {pickerOpen && (
                    <AssetPicker
                        title="Choose a logo"
                        onSelect={(url) => {
                            setLogo(url);
                            setSaveError('');
                            setPickerOpen(false);
                        }}
                        onClose={() => setPickerOpen(false)}
                    />
                )}
                {toast.node}
            </div>
        </AreaShell>
    );
}
